import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Bell, Wrench, CheckCircle, Calendar, AlertTriangle } from 'lucide-react';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Add notification
  const addNotification = (notification) => {
    const newNotification = {
      id: Date.now(),
      timestamp: new Date(),
      read: false,
      ...notification
    };

    setNotifications(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);

    // Show toast notification
    showToastNotification(newNotification);

    // Play notification sound (browser notification API)
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Heelpy - ' + newNotification.title, {
        body: newNotification.message,
        icon: '/logo.png',
        badge: '/logo.png'
      });
    }
  };

  const showToastNotification = (notification) => {
    const iconMap = {
      new_intervention: <Wrench className="w-5 h-5" />,
      status_change: <CheckCircle className="w-5 h-5" />,
      reminder: <Calendar className="w-5 h-5" />,
      urgent: <AlertTriangle className="w-5 h-5" />
    };

    const icon = iconMap[notification.type] || <Bell className="w-5 h-5" />;

    toast(notification.title, {
      description: notification.message,
      icon: icon,
      duration: 5000,
      action: notification.action ? {
        label: notification.action.label,
        onClick: notification.action.onClick
      } : undefined
    });
  };

  // Mark as read
  const markAsRead = (id) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === id ? { ...notif, read: true } : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  // Mark all as read
  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notif => ({ ...notif, read: true }))
    );
    setUnreadCount(0);
  };

  // Clear notification
  const clearNotification = (id) => {
    const notification = notifications.find(n => n.id === id);
    if (notification && !notification.read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  // Clear all notifications
  const clearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Simulate real-time notifications for demo (remove when backend is ready)
  useEffect(() => {
    const user = localStorage.getItem('user');
    if (!user) return;

    const userData = JSON.parse(user);

    // Simulate notifications for technicians
    if (userData.role === 'technician') {
      const intervalId = setInterval(() => {
        // Random notification every 2-5 minutes (for demo)
        const shouldNotify = Math.random() > 0.7;
        if (shouldNotify) {
          const notifications = [
            {
              type: 'reminder',
              title: 'Rappel intervention',
              message: 'Votre prochaine intervention commence dans 30 minutes',
              priority: 'medium'
            },
            {
              type: 'status_change',
              title: 'Mise à jour',
              message: 'Le client a confirmé son RDV pour demain',
              priority: 'low'
            }
          ];
          const randomNotif = notifications[Math.floor(Math.random() * notifications.length)];
          addNotification(randomNotif);
        }
      }, 120000); // Every 2 minutes

      return () => clearInterval(intervalId);
    }

    // Simulate notifications for admin
    if (userData.role === 'admin') {
      const intervalId = setInterval(() => {
        const shouldNotify = Math.random() > 0.6;
        if (shouldNotify) {
          const notifications = [
            {
              type: 'new_intervention',
              title: 'Nouvelle demande',
              message: 'Un nouveau client a demandé une réparation',
              priority: 'high'
            },
            {
              type: 'urgent',
              title: 'Intervention urgente',
              message: 'Intervention prioritaire à attribuer',
              priority: 'high'
            }
          ];
          const randomNotif = notifications[Math.floor(Math.random() * notifications.length)];
          addNotification(randomNotif);
        }
      }, 180000); // Every 3 minutes

      return () => clearInterval(intervalId);
    }
  }, [addNotification]);

  const value = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    clearNotification,
    clearAll
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
